import { useState } from 'react'
import {
  CalendarDays,
  ChevronDown,
  Clock3,
  SlidersHorizontal,
  Trash2,
  User,
  X,
} from 'lucide-react'

import { PROCEDURE_OPTIONS } from '../constants/app'

export default function PatientFilterModal({ filters, onApply, onClear, onClose }) {
  const [draft, setDraft] = useState(filters)

  function updateDraft(field, value) {
    setDraft((current) => ({
      ...current,
      [field]: value,
    }))
  }

  function togglePaymentType(value) {
    updateDraft('payment_type', draft.payment_type === value ? '' : value)
  }

  function handleSubmit(event) {
    event.preventDefault()
    onApply(draft)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#1A1A1A]/35 p-4 backdrop-blur-[5px]"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="filter-modal my-6 w-full max-w-[680px] overflow-hidden rounded-xl bg-white shadow-[0_22px_55px_rgba(17,24,39,0.22)] sm:max-w-[580px]"
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex min-h-[64px] items-center justify-between gap-3 border-b border-[#E5DED3] px-4 sm:min-h-[76px] sm:px-8">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F3E8CE] text-[#8A6819] sm:h-10 sm:w-10">
              <SlidersHorizontal size={18} strokeWidth={2.4} aria-hidden="true" />
            </div>
            <h2 className="min-w-0 text-base font-semibold leading-tight tracking-normal text-[#1A1A1A] sm:text-lg sm:text-2xl">
              Filtros de Pesquisa
            </h2>
          </div>
          <button
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#1A1A1A] transition hover:bg-[#FBF8F4] sm:h-10 sm:w-10"
            type="button"
            aria-label="Fechar filtros"
            onClick={onClose}
          >
            <X size={16} strokeWidth={2.1} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 px-4 py-5 sm:gap-5 sm:px-8 sm:py-6">
            <FilterField label="Nome do Paciente">
              <div className="relative">
                <User
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#3D3329]"
                  size={19}
                  strokeWidth={2.2}
                  aria-hidden="true"
                />
                <input
                  className="modal-input modal-input-with-icon"
                  placeholder="Ex: Maria Silva"
                  value={draft.name}
                  onChange={(event) => updateDraft('name', event.target.value)}
                />
              </div>
            </FilterField>

            <div className="grid gap-4 md:grid-cols-2">
              <FilterField label="Procedimento">
                <div className="relative">
                  <select
                    className="modal-input modal-input-select appearance-none"
                    value={draft.procedure}
                    onChange={(event) => updateDraft('procedure', event.target.value)}
                  >
                    <option value="">Selecione um procedimento</option>
                    {PROCEDURE_OPTIONS.map((procedure) => (
                      <option value={procedure} key={procedure}>
                        {procedure}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#3D3329]"
                    size={18}
                    strokeWidth={2.3}
                    aria-hidden="true"
                  />
                </div>
              </FilterField>

              <FilterField label="Tipo de Atendimento">
                <div className="grid h-[2.875rem] grid-cols-2 rounded-lg border border-[#D6CDBF] bg-white p-1">
                  <FilterPaymentOption
                    isActive={draft.payment_type === 'particular'}
                    label="Particular"
                    onClick={() => togglePaymentType('particular')}
                  />
                  <FilterPaymentOption
                    isActive={draft.payment_type === 'convenio'}
                    label="Convênio"
                    onClick={() => togglePaymentType('convenio')}
                  />
                </div>
              </FilterField>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FilterField label="Faixa de Valor (R$)">
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
                  <input
                    className="modal-input modal-input-compact"
                    min="0"
                    placeholder="R$ Min"
                    step="0.01"
                    type="number"
                    value={draft.min_value}
                    onChange={(event) => updateDraft('min_value', event.target.value)}
                  />
                  <span className="text-sm font-semibold text-[#5A5147]">-</span>
                  <input
                    className="modal-input modal-input-compact"
                    min="0"
                    placeholder="R$ Máx"
                    step="0.01"
                    type="number"
                    value={draft.max_value}
                    onChange={(event) => updateDraft('max_value', event.target.value)}
                  />
                </div>
              </FilterField>

              <FilterField label="Tempo Estimado (min)">
                <div className="relative">
                  <Clock3
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#3D3329]"
                    size={19}
                    strokeWidth={2.2}
                    aria-hidden="true"
                  />
                  <input
                    className="modal-input modal-input-with-icon"
                    min="1"
                    placeholder="Ex: 45"
                    type="number"
                    value={draft.duration_minutes}
                    onChange={(event) => updateDraft('duration_minutes', event.target.value)}
                  />
                </div>
              </FilterField>
            </div>

            <FilterField label="Data do Agendamento">
              <div className="grid items-center gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                <DateFilterInput
                  value={draft.start_date}
                  onChange={(value) => updateDraft('start_date', value)}
                />
                <span className="text-center text-sm font-semibold text-[#5A5147]">até</span>
                <DateFilterInput
                  value={draft.end_date}
                  onChange={(value) => updateDraft('end_date', value)}
                />
              </div>
            </FilterField>
          </div>

          <div className="flex min-h-[76px] flex-col-reverse items-stretch justify-end gap-3 border-t border-[#E5DED3] bg-[#FBFAF8] px-4 py-4 sm:flex-row sm:items-center sm:px-8 sm:py-0">
            <button
              className="flex h-10 items-center justify-center gap-2 rounded-full border border-[#D6CDBF] bg-white px-5 text-sm font-bold tracking-[0.02em] text-[#5A5147] transition hover:bg-[#FBF8F4]"
              type="button"
              onClick={onClear}
            >
              <Trash2 size={16} strokeWidth={2.2} aria-hidden="true" />
              Limpar Filtros
            </button>
            <button
              className="flex h-10 items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-5 text-sm font-bold tracking-[0.02em] text-white shadow-sm transition hover:bg-[#2C2A28]"
              type="submit"
            >
              <SlidersHorizontal size={17} strokeWidth={2.4} aria-hidden="true" />
              Aplicar Filtros
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function FilterField({ children, label }) {
  return (
    <label className="grid gap-2">
      <span className="text-sm font-semibold uppercase tracking-[0.14em] text-[#1A1A1A]">
        {label}
      </span>
      {children}
    </label>
  )
}

function FilterPaymentOption({ isActive, label, onClick }) {
  return (
    <button
      className={`rounded-md text-sm font-bold tracking-[0.08em] transition ${
        isActive
          ? 'bg-[#1A1A1A] text-white shadow-[0_6px_16px_rgba(26,26,26,0.16)]'
          : 'text-[#1A1A1A] hover:bg-[#F8E1E7]'
      }`}
      type="button"
      onClick={onClick}
    >
      {label}
    </button>
  )
}

function DateFilterInput({ onChange, value }) {
  return (
    <div className="modal-date-field relative">
      <CalendarDays
        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#3D3329]"
        size={19}
        strokeWidth={2.2}
        aria-hidden="true"
      />
      <input
        className="modal-input modal-input-with-icon"
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}
